import { useState } from 'react';
import { useSession } from '@/auth/index';
import {
  Card,
  Text,
  Select,
  SelectOption,
  styled,
  Divider,
} from '@/theme/index';

interface AccountInfoProps {
  /** The list of options to display in the dropdown. Each option is a <i>SelectOption</i> (See @/theme/Select) */
  editOptions: SelectOption[];
  /** Callback fired when an option is selected */
  editOptionSelected?: (editing: string | null) => void;
  /** Callback fired when an option is clicked */
  editOptionClick?: (editing: string | null) => void;
}

const SelectMenu = styled(Select)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const TopSection = styled('div')(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

/**
 * A card that displays basic info about the user's membership and a
 * dropdown with links to edit various account details.
 */
export function AccountInformation({
  editOptions,
  editOptionSelected,
  editOptionClick,
}: AccountInfoProps) {
  const {
    session: { account },
  } = useSession();

  const [editing, setEditing] = useState<string>('');

  function handleOptionChanged(event: string) {
    setEditing(event);
    editOptionSelected?.(event);
  }

  function handleOptionClicked(event: string) {
    setEditing(event);
    editOptionClick?.(event);
  }
  const isAccountActive =
    account?.account?.subscription_info.subscription_state.toLocaleLowerCase() ===
    'active';

  return (
    <Card variant="outlined">
      <TopSection>
        <Text variant="h1">Account Information</Text>
        <Text variant="body2" fontWeight="bold" mb={1.5}>
          Member ID
          <Text
            fontWeight="bold"
            sx={{ display: 'inline', marginLeft: 1.5 }}
            component="span"
          >
            {account?.account.user_info.user_code}
          </Text>
        </Text>
        <Text variant="body2" fontWeight="bold" mb={1.5}>
          {account?.account.user_info.email}
        </Text>
        {isAccountActive && (
          <Text variant="body2">Your membership is active</Text>
        )}
      </TopSection>
      <Divider sx={{ marginBottom: 1 }} />
      <Text variant="body1" fontWeight="bold">
        Edit Account Information
      </Text>
      <SelectMenu
        id="edit-account-info"
        name="edit-account-info"
        value={editing}
        options={editOptions}
        onOptionSelect={handleOptionChanged}
        onOptionClick={handleOptionClicked}
        placeholder="Edit Account Information"
      />
    </Card>
  );
}
